import React, { useState, useEffect } from 'react';
import Modal from '../../../../../../../../shared/design-system/components/atoms/modal';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import Input from '../../../../../../../../shared/design-system/components/input/input';
import { supportUrls } from '../../../../../../../../shared/utils/urls';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import type {
  PaddlePreviewSubscriptionResponse,
  SubscriptionUpdateType,
} from '../../../../types/paddle';
import ImageIcon from '../../../../../../../../shared/components/images/image-icon';
import { Images } from '../../../../../../../../shared/app-constants';
import { getCardIcon } from '../../../../helpers/get-card-icon';

type LeadFinderBillingDetails = {
  updateType: SubscriptionUpdateType;
  displayPlanName: string;
  billingPeriod: 'month' | 'year';
  basePlanAmount: number;
  addonCredits?: number;
  addonAmount?: number;
  basePaddlePriceId?: string;
};

type Props = {
  show: boolean;
  onClose: () => void;
  planName: string;
  previewData: PaddlePreviewSubscriptionResponse | null;
  onPayNow: () => void;
  isPurchaseInProgress: boolean;
  isPreviewLoading: boolean;
  onUpdateCardDetails?: () => void;
  isUpdatingPaymentMethod?: boolean;
  onApplyCoupon?: (couponCode: string) => void;
  onClearCoupon?: () => void;
  isCouponApplying?: boolean;
  isCouponApplied?: boolean;
  isCouponInvalid?: boolean;
  leadFinderBilling?: LeadFinderBillingDetails;
};

const currencySymbolMap: Record<string, string> = {
  USD: '$',
  EUR: '\u20AC',
  GBP: '\u00A3',
  SEK: 'kr',
  AUD: 'A$',
  CAD: 'C$',
  INR: '\u20B9',
  BRL: 'R$',
  MXN: 'MX$',
  JPY: '\u00A5',
  CNY: '\u00A5',
};

const getCurrencySymbol = (currency?: string): string => {
  if (!currency) return '$';
  return currencySymbolMap[currency.toUpperCase()] || currency;
};

const formatAmount = (amount: number, currency?: string): string => {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${amount.toFixed(2)}`;
};

const formatCreditsLabel = (credits: number): string => {
  if (credits >= 1000) return `${credits / 1000}K`;
  return credits.toString();
};

const PaddleBillingSummaryModal: React.FC<Props> = ({
  show,
  onClose,
  planName,
  previewData,
  onPayNow,
  isPurchaseInProgress,
  isPreviewLoading,
  onUpdateCardDetails,
  isUpdatingPaymentMethod,
  onApplyCoupon,
  onClearCoupon,
  isCouponApplying,
  isCouponApplied,
  isCouponInvalid,
  leadFinderBilling,
}) => {
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  const upcomingPayment = previewData?.payload?.upcomingPayment;
  const cardDetails = previewData?.payload?.cardDetails?.[0];
  const currency = upcomingPayment?.currency;
  const hasMultipleItems = previewData?.payload?.hasMultipleItems;
  const lineItems = previewData?.payload?.lineItems;

  const getBillingPeriodLabel = (): string => {
    if (!planName) return '';
    const lower = planName.toLowerCase();
    if (lower.includes('monthly')) return '/month';
    if (lower.includes('yearly') || lower.includes('annual')) return '/year';
    return '';
  };

  const renderLoader = () => (
    <div className="loader-container d-flex justify-content-center align-items-center">
      <ImageIcon src={Images.Loader} />
    </div>
  );

  const getLeadFinderPlanDisplayName = (): string => {
    if (!leadFinderBilling) return planName;
    const { displayPlanName, billingPeriod, updateType } = leadFinderBilling;
    const periodLabel = billingPeriod === 'month' ? 'Monthly' : 'Yearly';
    const suffix = updateType === 'addon_only' ? ' (Current)' : '';
    return `${displayPlanName} (${periodLabel})${suffix}`;
  };

  const renderLeadFinderBillingLines = () => {
    if (!leadFinderBilling) return null;
    const { updateType, addonCredits, billingPeriod } = leadFinderBilling;
    const periodSuffix = `/${billingPeriod}`;

    const fmt = (amount: number) => formatAmount(amount, currency);

    // Match line items by priceId instead of relying on array index order.
    const baseLineItem = lineItems?.find(
      (item) => item.priceId === leadFinderBilling.basePaddlePriceId,
    );
    const addonLineItem = lineItems?.find(
      (item) => item.priceId !== leadFinderBilling.basePaddlePriceId,
    );

    const getBasePriceDisplay = () => {
      if (isPreviewLoading) return renderLoader();
      if (hasMultipleItems && baseLineItem) {
        return `${fmt(baseLineItem.unitPrice)}${periodSuffix}`;
      }
      if (lineItems?.length > 0) {
        return `${fmt((baseLineItem || lineItems[0]).unitPrice)}${periodSuffix}`;
      }
      return `${fmt(leadFinderBilling.basePlanAmount)}${periodSuffix}`;
    };

    const getAddonPriceDisplay = () => {
      if (isPreviewLoading) return renderLoader();
      if (hasMultipleItems && addonLineItem) {
        return `${fmt(addonLineItem.unitPrice)}${periodSuffix}`;
      }
      if (leadFinderBilling.addonAmount != null) {
        return `${fmt(leadFinderBilling.addonAmount)}${periodSuffix}`;
      }
      return '';
    };

    return (
      <>
        {/* Plan Amount */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="regular-1 gray-txt-12">
            {updateType === 'addon_only'
              ? 'Plan Amount (already paid)'
              : 'Plan Amount'}
          </span>
          <span
            className={`semibold-2 popover-arrow-color-txt${
              updateType === 'addon_only'
                ? ' paddle-billing-summary-modal__strikethrough'
                : ''
            }`}
          >
            {getBasePriceDisplay()}
          </span>
        </div>

        {/* Add-on Credits - shown for addon_only and base_with_addon */}
        {updateType !== 'base_only' && addonCredits != null && addonCredits > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <span className="regular-1 gray-txt-12">
              Add-on Credits ({formatCreditsLabel(addonCredits)}
              {periodSuffix})
            </span>
            <span className="semibold-2 popover-arrow-color-txt">
              {getAddonPriceDisplay()}
            </span>
          </div>
        )}

        {/* Total Amount */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="regular-1 gray-txt-12">Total Amount</span>
          <span className="semibold-2 popover-arrow-color-txt">
            {isPreviewLoading
              ? renderLoader()
              : formatAmount(
                  upcomingPayment?.subTotalWithoutTax ?? 0,
                  currency,
                )}
          </span>
        </div>

        {/* Payable Tax */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="regular-1 gray-txt-12">Payable Tax</span>
          <span className="semibold-2 popover-arrow-color-txt">
            {isPreviewLoading
              ? renderLoader()
              : formatAmount(upcomingPayment?.taxAmount ?? 0, currency)}
          </span>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (!show) {
      setShowCouponInput(false);
      setCouponCode('');
    }
  }, [show]);

  const handleApplyCoupon = () => {
    if (!couponCode.trim() || !onApplyCoupon) return;
    onApplyCoupon(couponCode.trim());
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    if (isCouponApplied) {
      // Coupon is active on preview — refresh preview without coupon (keep input open for spinner)
      onApplyCoupon?.('');
    } else {
      // Coupon was invalid — just clear state, no API call needed
      setShowCouponInput(false);
      onClearCoupon?.();
    }
  };

  const getInputVariant = () => {
    if (isCouponApplied) return Input.Variant.Succeeded;
    if (isCouponInvalid) return Input.Variant.Error;
    return undefined;
  };

  const renderCardDisplay = () => {
    if (!cardDetails?.card) return null;

    const { card } = cardDetails;
    const cardType = card.type || 'Card';
    const maskedNumber = `**** **** **** ${card.last4}`;

    return (
      <div className="paddle-billing-summary-modal__payment-section">
        <div className="d-flex justify-content-between align-items-center">
          <span className="regular-1 gray-txt-12">Default Payment Method</span>
        </div>
        <div className="paddle-billing-summary-modal__payment-card-box">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <span className="paddle-billing-summary-modal__card-brand">
                {getCardIcon(cardType)}
              </span>
              <span className="paddle-billing-summary-modal__card-number">
                {maskedNumber}
              </span>
            </div>
          </div>
        </div>
        {onUpdateCardDetails && (
            <span
              className={`semibold-1 mt-4 ${isUpdatingPaymentMethod ? 'gray-txt-12' : 'pointer'}`}
              role="button"
              tabIndex={0}
              onClick={isUpdatingPaymentMethod ? undefined : onUpdateCardDetails}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isUpdatingPaymentMethod) onUpdateCardDetails();
              }}
            >
            {isUpdatingPaymentMethod ? (
              <>
                <ImageIcon src={Images.Loader} alt="loading" className="paddle-billing-summary-modal__inline-loader" />
                Updating...
              </>
            ) : (
              '+  Change payment method'
            )}
            </span>
          )}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      className="billing-summary-modal paddle-billing-summary-modal"
      showCloseIcon={true}
      onClose={onClose}
      hideFooter={true}
      backdrop="static"
      titleContent="Billing Summary"
    >
      <div className="d-flex justify-content-between align-items-center">
        <span className="regular-1 gray-txt-12">Plan Name</span>
        <span className="regular-2 font-medium popover-arrow-color-txt">
          {leadFinderBilling ? getLeadFinderPlanDisplayName() : planName}
        </span>
      </div>

      {/* eslint-disable-next-line no-nested-ternary */}
      {leadFinderBilling ? (
        renderLeadFinderBillingLines()
      ) : hasMultipleItems && lineItems?.length > 0 ? (
        <>
          {lineItems.map((item, index) => (
            <div
              key={item.priceId}
              className="d-flex justify-content-between align-items-center mt-2"
            >
              <span className="regular-1 gray-txt-12">
                {index === 0
                  ? `Plan Amount${
                      upcomingPayment?.currentPlanLessAmount > 0
                        ? ' (already paid)'
                        : ''
                    }`
                  : item.productName}
              </span>
              <span className="semibold-2 popover-arrow-color-txt">
                {isPreviewLoading
                  ? renderLoader()
                  : `${formatAmount(item.unitPrice, currency)}${getBillingPeriodLabel()}`}
              </span>
            </div>
          ))}

          <div className="d-flex justify-content-between align-items-center mt-2">
            <span className="regular-1 gray-txt-12">Total Amount</span>
            <span className="semibold-2 popover-arrow-color-txt">
              {isPreviewLoading
                ? renderLoader()
                : formatAmount(
                    upcomingPayment?.subTotalWithoutTax ?? 0,
                    currency,
                  )}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <span className="regular-1 gray-txt-12">Plan Amount</span>
            <span className="semibold-2 popover-arrow-color-txt">
              {isPreviewLoading
                ? renderLoader()
                : formatAmount(
                    upcomingPayment?.subTotalWithoutTax ?? 0,
                    currency,
                  )}
            </span>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <span className="regular-1 gray-txt-12">Payable Tax</span>
            <span className="semibold-2 popover-arrow-color-txt">
              {isPreviewLoading
                ? renderLoader()
                : formatAmount(upcomingPayment?.taxAmount ?? 0, currency)}
            </span>
          </div>
        </>
      )}

      {upcomingPayment?.discountedPrice > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="regular-1 gray-txt-12">Discount</span>
          <span className="semibold-2 green-txt-5">
            {isPreviewLoading
              ? renderLoader()
              : `-${formatAmount(upcomingPayment.discountedPrice, currency)}`}
          </span>
        </div>
      )}

      {!leadFinderBilling && upcomingPayment?.currentPlanLessAmount > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="regular-1 gray-txt-12">
            Unused Current Plan Credit
          </span>
          <span className="semibold-2 popover-arrow-color-txt">
            -{formatAmount(upcomingPayment.currentPlanLessAmount, currency)}
          </span>
        </div>
      )}

      {onApplyCoupon && (
        <div className="promo-code-container">
          {!showCouponInput && !isCouponApplied && (
            <div className="d-flex flex-column align-items-end">
              <span
                role="button"
                className="font-10 font-medium blue-txt-11 mt-1 pointer"
                {...accessibleOnClick(() => setShowCouponInput(true))}
              >
                Have a discount code?
              </span>
            </div>
          )}

          {showCouponInput && (
            <div className="coupon-code-input-wrapper mt-3">
              <Input
                name="couponCode"
                placeholder="Enter Coupon"
                value={couponCode}
                variant={getInputVariant()}
                onChange={(value) => setCouponCode(value)}
                className="coupon-code-input"
                button={
                  !isCouponApplying && !isCouponApplied && !isCouponInvalid
                    ? { buttonTitle: 'Apply', onClick: handleApplyCoupon }
                    : undefined
                }
                icons={[
                  ...(isCouponApplying
                    ? [
                        {
                          place: Input.IconPlace.Right,
                          identifier: 'spinner-alt',
                          colorDefault: true,
                          className: 'icon-loading',
                        },
                      ]
                    : []),
                  ...((isCouponApplied || isCouponInvalid)
                    ? [
                        {
                          place: Input.IconPlace.Right,
                          identifier: 'trash',
                          colorDefault: true,
                          className: 'icon-1 icon-2 pointer',
                          onClick: handleRemoveCoupon,
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </div>
      )}

      <div className="divider-bottom mt-3 mb-3" />

      <div className="d-flex justify-content-between align-items-center">
        <span className="semibold-1 popover-arrow-color-txt">
          Total Amount to pay today
        </span>
        <span className="semibold-2 popover-arrow-color-txt">
          {isPreviewLoading
            ? renderLoader()
            : formatAmount(upcomingPayment?.amountToPay ?? 0, currency)}
        </span>
      </div>

      {renderCardDisplay()}

      {leadFinderBilling && (
        <div className="billing-summary-next-cycle mt-2">
          <div className="semibold-1 font-10">
            Next billing cycle:{' '}
            <span className="regular-1">
              {isPreviewLoading
                ? <ImageIcon src={Images.Loader} alt="loading" className="paddle-billing-summary-modal__inline-loader-no-margin" />
                : (() => {
                    const nextCycleAmount =
                      leadFinderBilling.updateType === 'addon_only' &&
                      hasMultipleItems &&
                      lineItems?.length > 0
                        ? lineItems.reduce(
                            (sum, item) => sum + item.subtotal,
                            0,
                          )
                        : (upcomingPayment?.subTotalWithoutTax ?? 0);
                    return `${formatAmount(nextCycleAmount, currency)}/${leadFinderBilling.billingPeriod}`;
                  })()}
            </span>
          </div>
          <div className="regular-1 font-10 blue-txt-11">
            50% of unused credits will rollover to the next{' '}
            {leadFinderBilling.billingPeriod}.
          </div>
        </div>
      )}

      <div className="mt-2">
        <p className="regular-1 font-10 gray-txt-15 text-center">
          Your subscription will auto-renew until you cancel. By upgrading, you
          agree to our{' '}
          <a
            href={supportUrls.refundPolicy}
            target="_blank"
            className="link"
            rel="noreferrer"
          >
            refund policy.
          </a>
        </p>
      </div>

      <Button
        onClick={onPayNow}
        variant={Button.Variant.Primary}
        isLoading={isPurchaseInProgress}
        disabled={isPurchaseInProgress || isPreviewLoading || isCouponApplying || isCouponInvalid}
        className="solid-primary-new w-100"
      >
        Pay Now
      </Button>
    </Modal>
  );
};

export default PaddleBillingSummaryModal;
